/**
 * Pricing & Inventory Centre · Inventory Health Workspace (PIC-102A §4).
 *
 * First-release workspace answering the FIVE business questions from
 * PIC-102 §1.1 at a glance:
 *
 *   Q1 · Is my inventory correct?
 *   Q2 · Can I trust today's numbers?
 *   Q3 · What requires investigation?
 *   Q4 · Is a purchase required?           (Q4 is a placeholder in V1 —
 *                                            resolves when PIC-103 Purchasing
 *                                            ships. See PIC-102A §1.)
 *   Q5 · What operational risks exist?
 *
 * Design principles preserved from Workforce · WF-107C:
 *   * Read-only header + hero cards on top; write actions inline where
 *     the decision is made (per-product Capture Reading; per-branch
 *     Certify).
 *   * Every write is idempotent-safe; the workspace re-fetches after
 *     any mutation.
 *   * Dual-signature Certification is visually prominent — one card
 *     per branch (Plant / Cardlock) with clear "Awaiting signature" vs
 *     "Signed by …" states.
 *   * Reversal is one gesture from the certified state, requires a
 *     reason.
 *   * Four-tone escalation (🟢🟡🟠🔴) surfaces in the Investigation
 *     feed and the Certify button state (🟠/🔴 blocks Certification per
 *     PIC-102A §5 invariant #3).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Drop, Gauge, WarningCircle, ShieldCheck, ArrowUUpLeft,
  Certificate, ArrowsClockwise, Clock,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/* ================================================================== */
/*  API                                                                */
/* ================================================================== */

const invApi = {
  products:        () => api.get("/pic/inventory-health/products"),
  fleet:           () => api.get("/pic/inventory-health/fleet-vehicles"),
  current:         () => api.get("/pic/inventory-health/operational-periods/current"),
  reconciliation:  (id) => api.get(`/pic/inventory-health/operational-periods/${id}/reconciliation`),
  captureReading:  (body) => api.post("/pic/inventory-health/operational-periods/current/readings", body),
  certifyPlant:    (id, body) => api.post(`/pic/inventory-health/operational-periods/${id}/certify/plant`, body || {}),
  certifyCardlock: (id, body) => api.post(`/pic/inventory-health/operational-periods/${id}/certify/cardlock`, body || {}),
  reverse:         (id, reason) => api.post(`/pic/inventory-health/operational-periods/${id}/reverse-certification`, { reason }),
  investigations:  (params) => api.get("/pic/inventory-health/investigations", { params }),
  dashboard:       () => api.get("/pic/inventory-health/dashboard/summary"),
};

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const PRODUCT_ORDER = ["regular", "premium", "clear_diesel", "dyed_diesel", "def"];

const TONE_META = {
  green:  { colour: "bg-emerald-500", label: "Expected variance" },
  yellow: { colour: "bg-amber-500",   label: "Operational investigation" },
  orange: { colour: "bg-orange-600",  label: "Management review" },
  red:    { colour: "bg-rose-600",    label: "Physical-integrity concern" },
};

function friendly(iso) {
  if (!iso) return "—";
  const d = iso.length === 10 ? new Date(`${iso}T00:00:00`) : new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
}

function friendlyLong(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function fmtL(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toLocaleString("en-CA", { maximumFractionDigits: 1 })} L`;
}

/* ================================================================== */
/*  Page                                                               */
/* ================================================================== */

export default function InventoryHealth() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Administrator" || user?.role === "Owner";
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(null);
  const [recon, setRecon] = useState(null);
  const [products, setProducts] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [captureDialog, setCaptureDialog] = useState({ open: false, productCode: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, p, invs] = await Promise.all([
        invApi.dashboard(),
        invApi.products(),
        invApi.investigations({ limit: 20 }),
      ]);
      setPeriod(d.data.period);
      setRecon(d.data.reconciliation);
      setProducts(p.data || []);
      setInvestigations(invs.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load Inventory Health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openInvestigations = useMemo(
    () => investigations.filter(i => i.status !== "closed"),
    [investigations],
  );
  const highestOpenTone = useMemo(() => {
    for (const t of ["red", "orange", "yellow", "green"]) {
      if (openInvestigations.some(i => i.tone === t)) return t;
    }
    return null;
  }, [openInvestigations]);
  const certBlocked = highestOpenTone === "red" || highestOpenTone === "orange";

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6" data-testid="inventory-health-page">
      <PageHeader loading={loading} period={period} onReload={load} />

      {period && (
        <>
          {/* Q1 + Q2 side by side */}
          <div className="grid gap-4 lg:grid-cols-2">
            <QuestionCard
              testId="ih-q1"
              icon={<Drop size={16} weight="regular" />}
              question="Q1 · Is my inventory correct?"
              helper="Per-product reconciliation for this Operational Period."
            >
              <ProductGrid
                products={products}
                byProduct={recon?.by_product || []}
                missing={recon?.missing_products || []}
                onCapture={(code) => setCaptureDialog({ open: true, productCode: code })}
                canCapture={isAdmin && period?.status !== "certified"}
              />
            </QuestionCard>

            <QuestionCard
              testId="ih-q2"
              icon={<Gauge size={16} weight="regular" />}
              question="Q2 · Can I trust today's numbers?"
              helper="Reading provenance + completeness for this period."
            >
              <TrustSummary
                products={products}
                byProduct={recon?.by_product || []}
                missing={recon?.missing_products || []}
                period={period}
              />
            </QuestionCard>
          </div>

          {/* Q3 · investigations */}
          <QuestionCard
            testId="ih-q3"
            icon={<WarningCircle size={16} weight="regular" />}
            question="Q3 · What requires investigation?"
            helper="Open investigations — anchored on physical integrity per PIC-102 B2.7."
            headerRight={
              highestOpenTone && (
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-white ${TONE_META[highestOpenTone].colour}`} data-testid="ih-highest-tone">
                  <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
                  Highest open · {TONE_META[highestOpenTone].label}
                </span>
              )
            }
          >
            <InvestigationList investigations={openInvestigations} />
          </QuestionCard>

          {/* Q4 · placeholder */}
          <QuestionCard
            testId="ih-q4"
            icon={<Clock size={16} weight="regular" />}
            question="Q4 · Is a purchase required?"
            helper="Placeholder — resolves when PIC-103 Purchasing Decisions ships."
          >
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm italic text-slate-500">
              Awaiting Purchasing Engine (PIC-103). Advisory purchase-decision surface will appear here.
            </div>
          </QuestionCard>

          {/* Q5 · risks */}
          <QuestionCard
            testId="ih-q5"
            icon={<ShieldCheck size={16} weight="regular" />}
            question="Q5 · What operational risks exist?"
            helper="Physical-integrity concerns, notification obligations, historical patterns."
          >
            <RisksSummary investigations={investigations} />
          </QuestionCard>

          {/* Certify */}
          <CertifySection
            period={period}
            isAdmin={isAdmin}
            certBlocked={certBlocked}
            highestOpenTone={highestOpenTone}
            onCertify={async (branch) => {
              try {
                await (branch === "plant" ? invApi.certifyPlant(period.id) : invApi.certifyCardlock(period.id));
                toast.success(`${branch === "plant" ? "Plant" : "Cardlock"} branch signed`);
                await load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Certification failed");
              }
            }}
            onReverse={async (reason) => {
              try {
                await invApi.reverse(period.id, reason);
                toast.success("Certification reversed — period is now under investigation");
                await load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Reversal failed");
              }
            }}
          />
        </>
      )}

      <CaptureReadingDialog
        open={captureDialog.open}
        productCode={captureDialog.productCode}
        products={products}
        period={period}
        onClose={() => setCaptureDialog({ open: false, productCode: null })}
        onSaved={async () => { setCaptureDialog({ open: false, productCode: null }); await load(); }}
      />
    </div>
  );
}

/* ================================================================== */
/*  Sub-components                                                     */
/* ================================================================== */

function PageHeader({ loading, period, onReload }) {
  const statusLabel = {
    open:                 "Awaiting readings",
    readings_captured:    "Readings captured",
    certified:            "Certified",
    under_investigation:  "Under investigation",
    blocked:              "Blocked",
  }[period?.status] || "—";
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Pricing & Inventory Centre
        </div>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">Inventory Health</h1>
        {period && (
          <p className="mt-1 text-sm text-slate-600" data-testid="ih-period-summary">
            <span className="font-semibold text-slate-800">{period.code}</span>
            {" · "}
            <span>{friendly(period.open_date)} → {friendly(period.close_date)} ({period.length_days}d · {period.reason})</span>
            {" · "}
            <span className="font-semibold text-slate-700">{statusLabel}</span>
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onReload}
        disabled={loading}
        data-testid="ih-reload"
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
      >
        <ArrowsClockwise size={13} weight="bold" className={loading ? "animate-spin" : ""} />
        Refresh
      </button>
    </div>
  );
}

function QuestionCard({ icon, question, helper, headerRight, children, testId }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
        <div className="flex items-center gap-2">
          <div className="text-slate-500">{icon}</div>
          <div>
            <div className="text-sm font-semibold text-slate-800">{question}</div>
            {helper && <div className="text-xs text-slate-500">{helper}</div>}
          </div>
        </div>
        {headerRight}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function ProductGrid({ products, byProduct, missing, onCapture, canCapture }) {
  const sorted = [...products].sort((a, b) => PRODUCT_ORDER.indexOf(a.code) - PRODUCT_ORDER.indexOf(b.code));
  const dataByCode = Object.fromEntries((byProduct || []).map(b => [b.product_code, b]));
  return (
    <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3" data-testid="ih-product-grid">
      {sorted.map(p => {
        const data = dataByCode[p.code];
        const isMissing = missing.includes(p.code);
        const isWeekly = p.reading_cadence === "weekly";
        return (
          <div
            key={p.code}
            data-testid={`ih-product-${p.code}`}
            className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-900">{p.label}</div>
              <div className="text-xs text-slate-500">
                {data
                  ? `Closing ${fmtL(data.closing_l)}${data.throughput_l ? ` · Throughput ${fmtL(data.throughput_l)}` : ""}`
                  : isWeekly
                    ? "Weekly cadence — read on Friday"
                    : isMissing ? "No reading yet" : "—"}
              </div>
            </div>
            {canCapture && (
              <button
                type="button"
                onClick={() => onCapture(p.code)}
                data-testid={`ih-capture-${p.code}`}
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Capture
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TrustSummary({ products, byProduct, missing, period }) {
  // Only per-OP products count toward the "captured / total" ratio and
  // toward the "Missing" hint. Weekly-cadence products (DEF) are shown
  // separately so the PO isn't confused by a 4/4-and-also-missing UX
  // (see testing-agent iter-21 design note).
  const perOP        = products.filter(p => p.reading_cadence === "per_operational_period");
  const perOPMissing = missing.filter(m => perOP.some(p => p.code === m));
  const weekly       = products.filter(p => p.reading_cadence === "weekly");
  const weeklyMissing = missing.filter(m => weekly.some(p => p.code === m));
  const captured = perOP.length - perOPMissing.length;
  const sources = new Set((byProduct || []).flatMap(b => b.sources || []));
  return (
    <div className="space-y-3">
      <div className="rounded-md bg-slate-50 px-3 py-2 text-sm">
        <div className="font-semibold text-slate-800" data-testid="ih-trust-completeness">
          {captured} / {perOP.length} per-OP products captured
        </div>
        <div className="mt-0.5 text-xs text-slate-500">
          {perOPMissing.length === 0
            ? "All expected per-OP readings present."
            : `Missing (per-OP): ${perOPMissing.join(", ")}`}
        </div>
        {weeklyMissing.length > 0 && (
          <div className="mt-1 text-xs italic text-slate-500" data-testid="ih-trust-weekly-pending">
            Weekly (pending Friday): {weeklyMissing.join(", ")}
          </div>
        )}
      </div>
      <div className="grid gap-2 text-xs">
        <ProvenanceRow label="Source instruments observed" value={[...sources].join(" · ") || "—"} testId="ih-trust-sources" />
        <ProvenanceRow label="Period certification status"
                       value={period?.status || "—"}
                       tone={period?.status === "certified" ? "green" : "amber"}
                       testId="ih-trust-cert-status" />
        <ProvenanceRow label="Certified by (plant)"    value={period?.certified_by_plant    || "Awaiting signature"} testId="ih-trust-plant" />
        <ProvenanceRow label="Certified by (cardlock)" value={period?.certified_by_cardlock || "Awaiting signature"} testId="ih-trust-cardlock" />
      </div>
    </div>
  );
}

function ProvenanceRow({ label, value, tone = "slate", testId }) {
  const cls = tone === "green" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-700";
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-1" data-testid={testId}>
      <span className="text-slate-500">{label}</span>
      <span className={`font-semibold ${cls}`}>{value}</span>
    </div>
  );
}

function InvestigationList({ investigations }) {
  if (!investigations.length) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="ih-invs-empty">
        🟢 No open investigations for this period — the engine is quiet.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-slate-100" data-testid="ih-invs-list">
      {investigations.map(i => (
        <li key={i.id} className="flex items-center gap-3 py-2">
          <span className={`h-2.5 w-2.5 rounded-full ${TONE_META[i.tone || "yellow"].colour}`} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900">
              {i.rule} · {i.product_code} · {TONE_META[i.tone || "yellow"].label}
            </div>
            <div className="text-xs text-slate-500">
              Opened {friendlyLong(i.triggered_at)}
              {i.financial_material_flag && <span className="ml-2 rounded-sm border border-amber-200 bg-amber-50 px-1 py-0.5 text-[10px] font-semibold text-amber-800">Financially material</span>}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function RisksSummary({ investigations }) {
  const red = investigations.filter(i => i.tone === "red");
  return (
    <div className="space-y-2 text-sm">
      {red.length === 0 ? (
        <div className="text-slate-600" data-testid="ih-risk-empty">
          🟢 No physical-integrity concerns recorded for this period.
        </div>
      ) : (
        <ul className="space-y-1" data-testid="ih-risk-list">
          {red.map(i => (
            <li key={i.id} className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2">
              <div className="font-semibold text-rose-800">{i.product_code} · {i.rule}</div>
              <div className="text-xs text-rose-700">{i.physical_integrity_concern?.concern || "Concern"}</div>
              {i.physical_integrity_concern?.notification_obligations?.length > 0 && (
                <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-rose-800">
                  Notify: {i.physical_integrity_concern.notification_obligations.join(", ")}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CertifySection({ period, isAdmin, certBlocked, highestOpenTone, onCertify, onReverse }) {
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reason, setReason] = useState("");
  const isCertified = period.status === "certified";
  return (
    <section className="rounded-lg border border-slate-800 bg-charcoal p-5 text-ink-inverse" data-testid="ih-certify-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Certificate size={16} weight="regular" />
            <h2 className="text-sm font-semibold uppercase tracking-wider">Certify this Operational Period</h2>
          </div>
          <p className="mt-1 text-xs text-slate-300">Dual signature required — Plant (Sean) + Cardlock (Kevin). Backup covers both branches when either is absent.</p>
        </div>
        {isCertified && isAdmin && (
          <button
            type="button"
            onClick={() => setReverseOpen(true)}
            data-testid="ih-reverse-open"
            className="inline-flex items-center gap-1.5 rounded-md border border-rose-400/40 bg-rose-500/20 px-3 py-1.5 text-xs font-semibold text-rose-100 hover:bg-rose-500/30"
          >
            <ArrowUUpLeft size={12} weight="bold" />
            Reverse Certification
          </button>
        )}
      </div>
      {certBlocked && (
        <div className="mt-3 rounded-md border border-rose-400/40 bg-rose-500/15 px-3 py-2 text-xs text-rose-100" data-testid="ih-cert-blocked">
          🔒 Certification blocked — open investigation at 🟠/🔴 tone ({TONE_META[highestOpenTone]?.label}). Resolve or reclassify before signing.
        </div>
      )}
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <BranchCard branch="plant"    label="Plant Branch"    signedBy={period.certified_by_plant}    signedAt={period.certified_at_plant}    disabled={!isAdmin || certBlocked || isCertified} onSign={() => onCertify("plant")} />
        <BranchCard branch="cardlock" label="Cardlock Branch" signedBy={period.certified_by_cardlock} signedAt={period.certified_at_cardlock} disabled={!isAdmin || certBlocked || isCertified} onSign={() => onCertify("cardlock")} />
      </div>

      {reverseOpen && (
        <div className="mt-4 rounded-md border border-slate-600 bg-slate-800/50 p-3" data-testid="ih-reverse-form">
          <label className="text-xs font-semibold text-slate-200">Reason for reversal <span className="text-rose-300">*</span></label>
          <input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. ERP re-post required — Clear Diesel reading suspect"
            data-testid="ih-reverse-reason"
            className="mt-1 w-full rounded-md border border-slate-600 bg-slate-900 px-2 py-1.5 text-sm text-white"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={() => setReverseOpen(false)} className="rounded-md border border-slate-600 bg-slate-900 px-3 py-1 text-xs font-semibold text-slate-200 hover:bg-slate-800">Cancel</button>
            <button
              type="button"
              disabled={!reason.trim()}
              onClick={async () => { const r = reason.trim(); setReason(""); setReverseOpen(false); await onReverse(r); }}
              data-testid="ih-reverse-submit"
              className="rounded-md border border-rose-400 bg-rose-500 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
            >
              Reverse Certification
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function BranchCard({ branch, label, signedBy, signedAt, disabled, onSign }) {
  const signed = !!signedBy;
  return (
    <div
      data-testid={`ih-branch-${branch}`}
      className={`rounded-md border p-3 ${signed ? "border-emerald-400/40 bg-emerald-500/15" : "border-slate-600 bg-slate-800/40"}`}
    >
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-300">{label}</div>
      <div className="mt-1 text-sm font-semibold text-white">
        {signed ? `Signed by ${signedBy}` : "Awaiting signature"}
      </div>
      {signedAt && <div className="text-[11px] text-slate-400">{friendlyLong(signedAt)}</div>}
      {!signed && (
        <button
          type="button"
          disabled={disabled}
          onClick={onSign}
          data-testid={`ih-sign-${branch}`}
          className="mt-2 w-full rounded-md border border-slate-100 bg-white px-3 py-1.5 text-xs font-semibold text-charcoal hover:bg-slate-100 disabled:opacity-50"
        >
          Sign {label}
        </button>
      )}
    </div>
  );
}

function CaptureReadingDialog({ open, productCode, products, period, onClose, onSaved }) {
  const [value, setValue] = useState("");
  const [source, setSource] = useState("bulk_plant");
  const [instrument, setInstrument] = useState("atg");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setValue("");
      setNotes("");
      // DEF is captured on the DEF truck via tote by default.
      if (productCode === "def") { setSource("def_truck"); setInstrument("tote"); }
      else                       { setSource("bulk_plant"); setInstrument("atg"); }
    }
  }, [open, productCode]);

  if (!open) return null;
  const product = products.find(p => p.code === productCode);

  const submit = async (e) => {
    e.preventDefault();
    if (!value) { toast.error("Value in litres is required"); return; }
    setSaving(true);
    try {
      await invApi.captureReading({
        operational_period_id: period.id,
        product_code: productCode,
        source, instrument,
        value_litres: parseFloat(value),
        notes: notes.trim() || null,
      });
      toast.success(`${product?.label || productCode} reading captured`);
      await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reading save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        data-testid="ih-capture-dialog"
        className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-xl"
      >
        <h3 className="text-base font-semibold text-slate-900">Capture reading</h3>
        <p className="mt-1 text-xs text-slate-500">
          {product?.label || productCode} · Operational Period <span className="font-semibold">{period?.code}</span>
        </p>

        <Field label="Source" testId="ih-capture-source">
          <select value={source} onChange={e => setSource(e.target.value)} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm">
            <option value="bulk_plant">Bulk plant tank</option>
            <option value="delivery_truck">Delivery truck</option>
            <option value="cardlock_transfer">Cardlock transfer</option>
            <option value="def_truck">DEF truck</option>
          </select>
        </Field>
        <Field label="Instrument" testId="ih-capture-instrument">
          <select value={instrument} onChange={e => setInstrument(e.target.value)} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm">
            <option value="atg">ATG display</option>
            <option value="pump_meter">Pump meter</option>
            <option value="dipstick">Dipstick</option>
            <option value="tote">Tote</option>
          </select>
        </Field>
        <Field label="Value (litres)" testId="ih-capture-value-field">
          <input
            type="number" step="0.1" required
            value={value}
            onChange={e => setValue(e.target.value)}
            data-testid="ih-capture-value"
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Notes (optional)">
          <input
            value={notes}
            onChange={e => setNotes(e.target.value)}
            data-testid="ih-capture-notes"
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          />
        </Field>

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={saving} data-testid="ih-capture-submit" className="rounded-md border border-slate-800 bg-charcoal px-3 py-1.5 text-sm font-semibold text-ink-inverse hover:bg-slate-900 disabled:opacity-50">
            {saving ? "Saving…" : "Save reading"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="mt-3 block text-xs font-semibold text-slate-700" data-testid={testId}>
      {label}
      <div className="mt-1 font-normal">{children}</div>
    </label>
  );
}
